import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';

const ConfirmContext = createContext(null);

const DEFAULT_OPTIONS = {
  title: 'Confirm?',
  message: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  variant: 'default',
  requireTypedConfirm: true,
  confirmText: 'confirm',
};

/**
 * Portal-wide confirmation provider. Mount once in `MyRoutes.js`.
 * Returns a Promise<boolean> — true when confirmed, false on cancel/Esc/scrim.
 *
 * Options (all optional except title/message as needed):
 *   requireTypedConfirm  default true — user must type confirmText before confirm enables
 *   confirmText          default 'confirm' — case-insensitive match
 *   variant              'default' | 'danger' — danger shows warning banner + red button
 */
export function ConfirmProvider({ children }) {
  const [dialogState, setDialogState] = useState(null);
  const resolveRef = useRef(null);

  const askConfirmation = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialogState({ ...DEFAULT_OPTIONS, ...options });
    });
  }, []);

  const settle = useCallback((result) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setDialogState(null);
  }, []);

  const handleCancel = useCallback(() => settle(false), [settle]);
  const handleConfirm = useCallback(() => settle(true), [settle]);

  return (
    <ConfirmContext.Provider value={askConfirmation}>
      {children}
      {dialogState && (
        <ConfirmDialog
          {...dialogState}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
        />
      )}
    </ConfirmContext.Provider>
  );
}

/** Async confirmation helper — must be used inside `ConfirmProvider`. */
export function useConfirm() {
  const askConfirmation = useContext(ConfirmContext);
  if (!askConfirmation) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return askConfirmation;
}
